/**
 * W93 Chart-Check Phase 3 — Watchlist-Badge: wichtigstes Chart-Ereignis
 * (z.B. "🔴 MA200-Rejection von unten") aus v5_chart_events, Stil wie
 * ScoreDeltaBadge. Tooltip: voller Ereignis-Satz + Nächste Marken.
 *
 * Self-contained: geteilte Query ['chart-events-all'] (eine Tabelle,
 * ~60 Rows) — kein Prop-Threading durch die Watchlist nötig.
 */

import { useDataState } from '@/hooks/use-data-state';
import { HoverTooltip } from '@/components/ui/hover-tooltip';
import { fetchChartEventsAll } from '@/lib/chart-events-read';
import { cn } from '@/lib/utils';

interface Props {
  symbol: string;
}

const TONE_CLS: Record<string, string> = {
  bearish: 'border-red-700/40 bg-red-950/30 text-red-300',
  constructive: 'border-emerald-700/40 bg-emerald-950/30 text-emerald-300',
  neutral: 'border-amber-700/40 bg-amber-950/30 text-amber-300',
};

const TONE_DOT: Record<string, string> = {
  bearish: '🔴',
  constructive: '🟢',
  neutral: '🟠',
};

export function ChartEventBadge({ symbol }: Props) {
  const all = useDataState({
    queryKey: ['chart-events-all'],
    queryFn: (signal) => fetchChartEventsAll(signal),
    ttlSeconds: 30 * 60,
    isEmpty: (m) => !m || m.size === 0,
  });
  const row = all.state.data?.get(symbol.toUpperCase()) ?? null;
  const ev = row?.top_event ?? null;
  if (!ev) return null;

  return (
    <HoverTooltip
      align="right"
      widthRem={24}
      content={
        <div className="space-y-1 text-[11px] leading-snug">
          <div className="font-semibold text-zinc-100">
            {TONE_DOT[ev.tone] ?? '🟠'} {ev.badge}
          </div>
          <div className="text-zinc-300">{ev.text}</div>
          {(row?.next_marks?.up_label || row?.next_marks?.down_label) && (
            <div className="text-zinc-400 border-t border-zinc-700 pt-1">
              ▸ {[row?.next_marks?.up_label, row?.next_marks?.down_label]
                .filter(Boolean)
                .join(' · ')}
            </div>
          )}
          <div className="text-[10px] text-zinc-500 italic">
            Chart-Check (deterministisch, täglich 18:15) — Details auf der Symbol-Seite.
          </div>
        </div>
      }
    >
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded border px-1.5 py-px text-[10px] max-w-[11rem]',
          TONE_CLS[ev.tone] ?? TONE_CLS.neutral
        )}
      >
        <span aria-hidden>{TONE_DOT[ev.tone] ?? '🟠'}</span>
        <span className="truncate">{ev.badge}</span>
      </span>
    </HoverTooltip>
  );
}
